import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, ShieldCheck } from "lucide-react";
import ModalShell from "@/components/site/ModalShell";
import { reveal } from "@/components/site/motionPresets";
import {
  PrivacyPolicyBody,
  TermsOfServiceBody,
} from "@/components/site/policyContent";

const POLICY_LINKS = [
  { id: "privacy", label: "Privacy Policy" },
  { id: "terms", label: "Terms of Service" },
];

/** Modal header. The eyebrow survives here because a modal genuinely needs
 *  orienting — you can arrive at one without page context. */
function PolicyModalHeader({ Icon, titleId, title }) {
  return (
    <div className="flex items-start gap-4 pr-11">
      <div className="grid h-[3.25rem] w-[3.25rem] flex-shrink-0 place-items-center rounded-md border border-[#10B981]/35 bg-[#10B981]/10 text-[#10B981]">
        <Icon size={23} strokeWidth={1.6} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#10B981]">
          OneVidaria Digital
        </p>
        <h3
          id={titleId}
          className="font-display mt-2 text-3xl font-normal tracking-tight text-white sm:text-5xl"
        >
          {title}
        </h3>
      </div>
    </div>
  );
}

function PolicyModal({ onClose, kind }) {
  const isPrivacy = kind === "privacy";
  return (
    <ModalShell
      onClose={onClose}
      labelledBy={isPrivacy ? "privacy-modal-title" : "terms-modal-title"}
      closeLabel={
        isPrivacy ? "Close Privacy Policy" : "Close Terms of Service"
      }
      maxWidthClassName="max-w-3xl"
    >
      <div>
        <PolicyModalHeader
          Icon={isPrivacy ? ShieldCheck : FileText}
          titleId={isPrivacy ? "privacy-modal-title" : "terms-modal-title"}
          title={isPrivacy ? "Privacy Policy" : "Terms of Service"}
        />
        <div className="mt-7">
          {isPrivacy ? <PrivacyPolicyBody /> : <TermsOfServiceBody />}
        </div>
        <div className="mt-7 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm bg-[#10B981] px-5 py-3 text-sm font-semibold text-[#06231A] transition-colors hover:bg-[#34D399]"
          >
            Close
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export default function Footer() {
  const [openPolicy, setOpenPolicy] = useState(null);

  const closePolicy = useCallback(() => setOpenPolicy(null), []);

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-line bg-surface-3 mt-12"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 sm:py-24">
        <motion.div
          {...reveal({ y: 16, duration: 0.8, margin: undefined })}
          className="relative"
        >
          {/* No "ONEVIDARIA DIGITAL" watermark above the lockup any more. It
              echoed the old text wordmark in the same typeface; above the new
              lockup it spelled the name a second time in a different face. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              {/* The reversed lockup is the name, so it carries the alt and
                  there is no separate text beside it to announce twice.
                  Sized by height: the lockup is ~1.66:1. h-24, not h-16: at
                  64px tall the tracked-out DIGITAL is ~5px and unreadable on a
                  1x display. */}
              <img
                src="/ovd-lockup-reversed.svg"
                alt="OneVidaria Digital"
                className="h-24 w-auto"
                data-testid="footer-lockup"
              />
              <p
                className="mt-4 text-sm text-white/65"
                data-testid="footer-location"
              >
                Las Piñas City, Philippines
              </p>
            </div>

            <div className="lg:col-span-4">
              <p className="text-sm font-medium text-white/65">Legal</p>
              <ul className="mt-2 flex flex-col" data-testid="footer-policy-list">
                {POLICY_LINKS.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => setOpenPolicy(l.id)}
                      data-testid={`footer-policy-${l.id}`}
                      aria-haspopup="dialog"
                      className="inline-flex min-h-[44px] items-center text-left text-sm text-white/65 transition-colors hover:text-white"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              <p className="text-sm font-medium text-white/65">Direct</p>
              <div className="mt-2 flex flex-col">
                <Link
                  to="/services"
                  data-testid="footer-services-link"
                  className="inline-flex min-h-[44px] items-center text-sm text-white/65 transition-colors hover:text-white"
                >
                  Services
                </Link>
                <a
                  href="mailto:contactus@onevidariadigital.com"
                  data-testid="footer-email-link"
                  className="inline-flex min-h-[44px] items-center text-sm text-white/65 hover:text-[#10B981] transition-colors"
                >
                  contactus@onevidariadigital.com
                </a>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-line">
            <p className="text-xs text-white/65" data-testid="footer-copyright">
              © 2026 OneVidaria Digital. All Rights Reserved.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Keys are load-bearing here — see the note in Ecosystem.jsx. */}
      <AnimatePresence>
        {openPolicy && (
          <PolicyModal
            key={openPolicy}
            kind={openPolicy}
            onClose={closePolicy}
          />
        )}
      </AnimatePresence>
    </footer>
  );
}
